import { useCallback, useEffect, useRef, useState } from "react";
import { Circle, Group, Image, Line } from "react-konva";
import useImage from "use-image";

interface ClockArmProps {
  length: number;
  img: string;
  width: number;
  scale: number;
  manualRotationEnabled?: boolean;
  position?: [number, number];
  offset?: [number, number];
  startRotation?: number;
  onManualRotateStart?: (
    event: MouseEvent | TouchEvent,
    centerOfRotation: [number, number]
  ) => void;
  onManualRotateEnd?: (
    event: MouseEvent | TouchEvent,
    centerOfRotation: [number, number]
  ) => void;
  onRotate?: (angle: number) => void;
  onCompletedRotation?: (direction: 1 | -1) => void;
}

export function ClockArm({
  length,
  width,
  img,
  scale,
  manualRotationEnabled = false,
  position = [0, 0],
  offset = [0, 0],
  startRotation = 0,
  onManualRotateStart: onHandleMouseDown,
  onManualRotateEnd: onHandleMouseUp,
  onCompletedRotation,
  onRotate,
}: ClockArmProps) {
  const [angle, setAngle] = useState(startRotation);
  const angleRef = useRef(0);
  const previousAngleRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState<[number, number]>([
    0, 0,
  ]);
  const [image] = useImage(img);
  const [offsetX, offsetY] = offset;
  const [x, y] = position;

  const handleStart = useCallback((evt: MouseEvent | TouchEvent): void => {
    console.log("handleStart", evt);
    console.log("rotation enabled", manualRotationEnabled);
    if (!manualRotationEnabled) return;
    const pos: [number, number] =
      "clientX" in evt
        ? [evt.clientX, evt.clientY]
        : [evt.targetTouches[0].clientX, evt.targetTouches[0].clientY];
    setDragStartPosition(pos);
    setIsDragging(true);
    onHandleMouseDown?.(evt, [x, y]);
  }, [manualRotationEnabled]);

  useEffect(() => {
    console.log("manualRotationEnabled", manualRotationEnabled);
  }, [manualRotationEnabled]);

  useEffect(() => {
    setAngle(startRotation);
  }, [startRotation]);

  useEffect(() => {
    if (!manualRotationEnabled) return;
    function handleMove(evt: MouseEvent | TouchEvent): void {
      if (!isDragging) return;
      let x: number, y: number;
      if ("clientX" in evt) {
        x = evt.clientX;
        y = evt.clientY;
      } else {
        x = evt.targetTouches[0].clientX;
        y = evt.targetTouches[0].clientY;
      }

      const angle =
        lookAt([x - position[0], y - position[1]]) * (180 / Math.PI);

      const newAngle = (angle + 90 + 360) % 360;
      previousAngleRef.current = angleRef.current;
      if (angleRef.current !== newAngle) {
        angleRef.current = newAngle;
        onRotate?.(newAngle);
      }

      setAngle(newAngle);

      if (previousAngleRef.current - newAngle > 180) {
        onCompletedRotation?.(1);
      } else if (previousAngleRef.current - newAngle < -180) {
        onCompletedRotation?.(-1);
      }
    }

    function handleEnd(_: MouseEvent | TouchEvent): void {
      if (isDragging) {
        setIsDragging(false);
        onHandleMouseUp?.(_, [x, y]);
      }
    }

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchend", handleEnd);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, dragStartPosition, position]);

  return (
    <Group rotation={angle} x={x} y={y} scale={{ x: scale, y: scale }}>
      {/* <Line
        points={[0, 0, 0, -length]}
        stroke={"blue"}
        strokeWidth={1}
        lineCap={"round"}
      /> */}
      <Image
        image={image}
        x={offsetX}
        y={offsetY}
        width={width}
        height={length}
        shadowColor={"black"}
        shadowBlur={3}
        shadowOffset={calculateShadowVectorFromAngle(60, 10)}
        shadowOpacity={0.5}
        onMouseDown={(evt) => handleStart(evt.evt)}
        onTouchStart={(evt) => handleStart(evt.evt)}
      />
      <Circle
        radius={40}
        stroke={"transparent"}
        strokeWidth={4}
        x={0}
        y={-length * 0.75}
        fill={"transparent"}
        onMouseDown={(evt) => handleStart(evt.evt)}
        onTouchStart={(evt) => handleStart(evt.evt)}
      />
    </Group>
  );
}

function calculateShadowVectorFromAngle(angle: number, dist = 10) {
  const radians = (angle * Math.PI) / 180;
  return { x: Math.cos(radians) * dist, y: Math.sin(radians) * dist };
}

function lookAt(target: [number, number]): number {
  return Math.atan2(target[1], target[0]);
}
